import { type AlmanacCableConsumer } from './CableConsumer/types'
import { backoffWithJitter, type CreateOptions } from '@anycable/core'
import { createConsumer } from '@anycable/web'
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'

import { useWebsocketUrl } from 'hooks/useWebsocketUrl'
import { DebugWebSocket } from './DebugWebSocket'
import { createDebugConsumer } from './CableConsumer/createDebugConsumer'

import { FALLBACK_CABLE_CONNECTION_URL, CABLE_CONNECTION_URL } from 'env'

export type WebsocketConsumer = AlmanacCableConsumer | null

const WebsocketContext = createContext<WebsocketConsumer>(null)

const CONSUMER_OPTIONS = {
  // Increase min WS poll interval from 3 to 5 seconds
  pingInterval: 5000,
  // reconnect strategy: backoffWithJitter with 50ms ping interval, with a max interval of 2 seconds
  reconnectStrategy: backoffWithJitter(50, { maxInterval: 2000 }),
  // TMP: need to see why it fails so much to reconnect
  logLevel: 'debug',
  websocketImplementation: localStorage.getItem('cable_debug_websocket') === 'true' ? DebugWebSocket : WebSocket,
} as Partial<CreateOptions>

export const WebsocketProvider = ({ children }: { children: ReactNode }) => {
  const { connectionUrl } = useWebsocketUrl()
  // We are facing network blocking issues with some plugins like Honey on Brave
  const { connectionUrl: fallbackConnectionUrl } = useWebsocketUrl(
    FALLBACK_CABLE_CONNECTION_URL ?? CABLE_CONNECTION_URL,
  )

  const [ws, setWs] = useState<WebsocketConsumer>(null)

  useEffect(() => {
    setWs((currentWs) => {
      currentWs?.cable.disconnect()
      if (localStorage.getItem('anycable_tp_client') === 'true') {
        return createConsumer(connectionUrl, CONSUMER_OPTIONS)
      }
      return createDebugConsumer(connectionUrl, CONSUMER_OPTIONS, fallbackConnectionUrl)
    })
  }, [connectionUrl, fallbackConnectionUrl])

  return <WebsocketContext.Provider value={ws}>{children}</WebsocketContext.Provider>
}

export const useWebsocketConnection = () => useContext(WebsocketContext)
